/**
 * 前端安全工具类
 * 用于防止XSS、CSRF等常见Web安全攻击
 */

/**
 * XSS防护：转义HTML特殊字符
 * @param {string} str - 需要转义的字符串
 * @returns {string} - 转义后的安全字符串
 */
export function escapeHtml(str) {
  if (typeof str !== 'string') return str;
  
  const htmlEscapeMap = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#x27;',
    '/': '&#x2F;'
  };
  
  return str.replace(/[&<>"'/]/g, (char) => htmlEscapeMap[char]);
}

/**
 * XSS防护：深度清理对象中的所有字符串字段
 * @param {Object} obj - 需要清理的对象
 * @returns {Object} - 清理后的对象
 */
export function sanitizeObject(obj) {
  if (typeof obj !== 'object' || obj === null) return obj;
  
  const sanitized = Array.isArray(obj) ? [] : {};
  
  for (const key in obj) {
    if (typeof obj[key] === 'string') {
      sanitized[key] = escapeHtml(obj[key]);
    } else if (typeof obj[key] === 'object') {
      sanitized[key] = sanitizeObject(obj[key]);
    } else {
      sanitized[key] = obj[key];
    }
  }
  
  return sanitized;
}

/**
 * 检测危险脚本模式
 * @param {string} str - 需要检测的字符串
 * @returns {boolean} - 是否包含危险模式
 */
export function detectXSS(str) {
  if (typeof str !== 'string') return false;
  
  const dangerousPatterns = [
    /<script[\s\S]*?>[\s\S]*?<\/script>/gi,
    /javascript:/gi,
    /on\w+\s*=/gi, // onclick=, onerror=, etc.
    /<iframe/gi,
    /eval\(/gi,
    /expression\(/gi
  ];
  
  return dangerousPatterns.some(pattern => pattern.test(str));
}

/**
 * 安全的localStorage存储（带加密）
 * 注意：这只是简单的Base64编码，不是真正的加密，但能防止明文泄露
 */
export const secureStorage = {
  /**
   * 安全存储数据
   * @param {string} key - 键名
   * @param {any} value - 值
   */
  setItem(key, value) {
    try {
      const data = JSON.stringify(value);
      const encoded = btoa(encodeURIComponent(data));
      window.localStorage.setItem(key, encoded);
    } catch (e) {
      console.error('存储失败:', e);
    }
  },
  
  /**
   * 安全读取数据
   * @param {string} key - 键名
   * @returns {any} - 解码后的值
   */
  getItem(key) {
    try {
      const encoded = window.localStorage.getItem(key);
      if (!encoded) return null;
      
      // 验证是否为有效的Base64字符串
      const base64Regex = /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/;
      if (!base64Regex.test(encoded)) {
        console.warn(`Key: ${key} 对应的数据不是有效的Base64，清除之。`);
        window.localStorage.removeItem(key);
        return null;
      }
      
      const data = decodeURIComponent(atob(encoded));
      return JSON.parse(data);
    } catch (e) {
      console.error('读取失败:', e);
      return null;
    }
  },
  
  /**
   * 删除数据
   * @param {string} key - 键名
   */
  removeItem(key) {
    window.localStorage.removeItem(key);
  },
  
  /**
   * 清空所有数据
   */
  clear() {
    window.localStorage.clear();
  }
};

/**
 * 密码强度验证
 * @param {string} password - 密码
 * @returns {Object} - {valid: boolean, strength: string, message: string}
 */
export function validatePasswordStrength(password) {
  if (!password || password.length < 6) {
    return {
      valid: false,
      strength: 'weak',
      message: '密码长度至少6位'
    };
  }
  
  let strength = 0;
  const checks = {
    length: password.length >= 8,
    lowercase: /[a-z]/.test(password),
    uppercase: /[A-Z]/.test(password),
    number: /[0-9]/.test(password),
    special: /[^a-zA-Z0-9]/.test(password)
  };
  
  strength = Object.values(checks).filter(Boolean).length;
  
  if (strength <= 2) {
    return {
      valid: true,
      strength: 'weak',
      message: '密码强度：弱（建议包含大小写字母、数字和特殊字符）'
    };
  } else if (strength <= 3) {
    return {
      valid: true,
      strength: 'medium',
      message: '密码强度：中'
    };
  } else {
    return {
      valid: true,
      strength: 'strong',
      message: '密码强度：强'
    };
  }
}

/**
 * 生成随机CSRF Token
 * @returns {string} - CSRF Token
 */
export function generateCSRFToken() {
  const array = new Uint8Array(32);
  window.crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * 清理敏感信息的日志
 * @param {Object} data - 要打印的数据
 * @returns {Object} - 清理后的数据
 */
export function sanitizeLog(data) {
  if (typeof data !== 'object' || data === null) return data;
  
  const sensitiveKeys = ['password', 'token', 'Authorization', 'phone', 'idCard'];
  const cleaned = JSON.parse(JSON.stringify(data));
  
  function maskSensitive(obj) {
    for (const key in obj) {
      if (sensitiveKeys.some(sensitive => key.toLowerCase().includes(sensitive.toLowerCase()))) {
        obj[key] = '******';
      } else if (typeof obj[key] === 'object' && obj[key] !== null) {
        maskSensitive(obj[key]);
      }
    }
  }
  
  maskSensitive(cleaned);
  return cleaned;
}

/**
 * URL参数安全过滤
 * @param {string} param - URL参数
 * @returns {string} - 过滤后的参数
 */
export function sanitizeUrlParam(param) {
  if (typeof param !== 'string') return param;
  
  // 移除危险字符
  return param
    .replace(/[<>'"]/g, '')
    .replace(/javascript:/gi, '')
    .replace(/on\w+=/gi, '');
}

/**
 * 防抖函数（防止暴力破解）
 * @param {Function} func - 要执行的函数
 * @param {number} wait - 等待时间（毫秒）
 * @returns {Function} - 防抖后的函数
 */
export function debounce(func, wait = 300) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

/**
 * 节流函数（限制请求频率）
 * @param {Function} func - 要执行的函数
 * @param {number} limit - 限制时间（毫秒）
 * @returns {Function} - 节流后的函数
 */
export function throttle(func, limit = 1000) {
  let inThrottle;
  return function(...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
}




// WEBPACK FOOTER //
// ./src/utils/security.js